import type { FC } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";

import { useOnEnter } from "app/custom-hooks/useOnKeyUp";
import { translate } from "app/filters/i18nFilter";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { Keyword } from "../types";

import { Volume } from "./Volume";

interface SuggestedKeywordProps {
    keyword: Keyword;
    onClick: VoidFunction;
    isAlreadyExists: boolean;
}

const { blue, carbon } = colorsPalettes;

const Container = styled.div<{ isClickAble: boolean }>`
    background-color: ${blue[50]};
    padding: 0px 48px 0px 16px;
    box-sizing: border-box;
    height: 44px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${({ isClickAble }) => isClickAble && `cursor: pointer;`};
    ${setFontVariant("input2", carbon["400"])}
    &:hover {
        transition: background-color 300ms ease-in-out;
        background-color: ${blue[100]};
    }
`;

const AddIcon = styled.div`
    background-color: ${blue[400]};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    color: ${carbon[0]};
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
`;

const Bold = styled.span`
    ${setFontVariant("subtitle2", carbon[400])}
`;

const Text = styled.span`
    ${setFontVariant("input2", carbon[300])}
`;

export const SuggestedKeyword: FC<SuggestedKeywordProps> = ({ keyword, onClick, isAlreadyExists }) => {
    const { term } = keyword;
    useOnEnter(onClick);
    const displayedText = isAlreadyExists
        ? translate("auto.keyword.list.generator.body.suggestions.already.exists")
        : term;
    return (
        <Container
            data-automation="suggested-keyword-container"
            onClick={onClick}
            isClickAble={!isAlreadyExists}
        >
            <FlexRow
                gap="8px"
                alignItems="center"
            >
                {isAlreadyExists ? (
                    <SWReactIcons
                        iconName="checked"
                        size="xs"
                        iconColor={blue[400]}
                    />
                ) : (
                    <AddIcon>+</AddIcon>
                )}
                <Bold> {displayedText}</Bold>
                {!isAlreadyExists ? (
                    <Text>{translate("auto.keyword.list.generator.body.suggestions.click.to.add")}</Text>
                ) : null}
            </FlexRow>
            <Volume keyword={keyword} />
        </Container>
    );
};
